import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, User } from "lucide-react";
import { RecordLinkRow } from "@/components/crm/RecordLinkRow";
import {
  crmAccountMatchReason,
  crmAccountMatchesQuery,
  formatCrmIndustryLabel,
  formatCrmSourceLabel,
  formatCrmStatusLabel,
} from "@/lib/crmDisplay";

export type CrmAccountsListRow = {
  id: string;
  name: string;
  accountKind?: "company" | "individual" | null;
  city?: string | null;
  state?: string | null;
  industry?: string | null;
  source?: string | null;
  status?: string | null;
  outreachState?: string | null;
  ownerId?: string | null;
  ownerStatus?: string | null;
  lastTouchAt?: string | null;
  domain?: string | null;
  email?: string | null;
  companyPhone?: string | null;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

type Props = {
  accounts: CrmAccountsListRow[];
  initialQuery?: string;
};

export function CrmAccountsListClient({ accounts, initialQuery = "" }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);
  const [page, setPage] = useState(1);

  const filteredAccounts = useMemo(() => {
    const q = query.trim();
    if (!q) return accounts;
    return accounts.filter((account) => crmAccountMatchesQuery(account, q));
  }, [accounts, query]);

  const totalPages = Math.max(1, Math.ceil(filteredAccounts.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = filteredAccounts.slice(start, start + pageSize);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search accounts by company, city, industry, owner, or status"
          className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          aria-label="Search accounts"
        />
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <span>Page size</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) as (typeof PAGE_SIZE_OPTIONS)[number]);
              setPage(1);
            }}
            className="rounded-md border border-gray-300 px-2 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
            <tr>
              <th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 font-semibold">Location</th>
              <th className="px-4 py-3 font-semibold">Industry</th>
              <th className="px-4 py-3 font-semibold">Source</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Owner</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((account) => {
              const matchReason = crmAccountMatchReason(account, query);
              const href = `/crm/accounts/${encodeURIComponent(account.id)}`;
              return (
                <RecordLinkRow key={account.id} href={href}>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      {account.accountKind === "individual" ? (
                        <User className="mt-0.5 size-4 shrink-0 text-indigo-600" aria-hidden />
                      ) : (
                        <Building2 className="mt-0.5 size-4 shrink-0 text-indigo-600" aria-hidden />
                      )}
                      <div>
                        <Link to={href} className="font-medium text-indigo-700 hover:underline dark:text-indigo-300">
                          {account.name}
                        </Link>
                        {matchReason ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Match: {matchReason}</p>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {[account.city, account.state].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3">{formatCrmIndustryLabel(account.industry) || "—"}</td>
                  <td className="px-4 py-3">{formatCrmSourceLabel(account.source) || "—"}</td>
                  <td className="px-4 py-3">{formatCrmStatusLabel(account.status) || "—"}</td>
                  <td className="px-4 py-3">{account.ownerId?.trim() || "Unassigned"}</td>
                </RecordLinkRow>
              );
            })}
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                  {query.trim() ? "No matching accounts." : "No accounts to show."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {filteredAccounts.length > pageSize ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Showing {start + 1}–{Math.min(start + pageSize, filteredAccounts.length)} of {filteredAccounts.length}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-40 dark:border-white/20"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-40 dark:border-white/20"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
